import Image from 'next/image'
import { Ficello } from '@captaincause/ui/Ficello'
import { tm } from '@captaincause/ui/tm'
import { StrapiLink } from 'types/strapi/link'
import { flattenImage } from 'utils/strapi/image'
import { Button } from 'components/Button'
import { Markdown } from 'components/Markdown'
import { StrapiImage } from 'components/StapiImage'
import type { FicelloStroke } from '@captaincause/ui/Ficello'
import type { ButtonProps } from 'components/Button'
import type { LocalImage, StrapiApiImage } from 'types/strapi/image'

type FicelloSet =
  | 'purple-green-orange'
  | 'green-orange-purple'
  | 'orange-purple-green'

export interface HeroProps {
  title: string | null
  description?: string | null
  primaryLink?: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
  secondaryLink?: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
  image?: StrapiApiImage | LocalImage | null
  ficellos?: FicelloSet
  titleClassName?: string
}

const FICELLOS_COLORS: Record<FicelloSet, FicelloStroke[]> = {
  'purple-green-orange': ['purple', 'green', 'orange'],
  'green-orange-purple': ['green', 'orange', 'purple'],
  'orange-purple-green': ['orange', 'purple', 'green'],
}

export default function Hero({
  title,
  description = '',
  primaryLink = null,
  secondaryLink = null,
  image: propsImage = null,
  titleClassName = undefined,
  ficellos = undefined,
}: HeroProps) {
  // eslint-disable-next-line no-nested-ternary
  const image = propsImage
    ? 'data' in propsImage
      ? flattenImage(propsImage)
      : propsImage
    : undefined

  const ficellosColors = ficellos ? FICELLOS_COLORS[ficellos] : undefined

  return (
    <div className="relative overflow-hidden bg-light-beige">
      {ficellosColors ? (
        <>
          <Ficello
            variant="virage"
            stroke={ficellosColors[0]}
            className={tm(
              'absolute left-[-900px] top-[200px] rotate-[-117deg] lg:left-[-700px] lg:top-[-1020px] xl:left-[-950px] xl:top-[-510px]',
              !image && 'lg:left-[-750px] lg:top-[-500px] xl:left-[-800px]',
            )}
          />
          <Ficello
            variant="camel"
            stroke={ficellosColors[1]}
            className={tm(
              'absolute right-[-900px] top-[250px] rotate-[-56deg] sm:top-[220px] md:right-[-400px] md:top-[230px] lg:right-[-420px] lg:top-[100px] xl:right-[-130px] xl:top-[70px]',
              !image && 'lg:right-[-500px] xl:right-[-400px] xl:top-[80px]',
            )}
          />
          <Ficello
            variant="boomerang"
            stroke={ficellosColors[2]}
            className={tm(
              'absolute right-[-700px] top-[-750px] rotate-[-150deg] sm:right-[-500px] sm:top-[-750px] lg:right-[-700px] lg:top-[-670px]',
              !image && 'lg:top-[-600px]',
            )}
          />
        </>
      ) : null}
      <div className="relative z-10 mx-auto flex max-w-screen-2xl flex-1 flex-col-reverse items-center justify-center gap-10 px-6 py-10 md:pb-10 lg:flex-row lg:gap-16 lg:px-10 lg:py-16">
        <div
          className={tm(
            'flex max-w-lg flex-1 flex-col items-center lg:items-start',
            !image && 'md:mx-auto',
          )}
        >
          {title ? (
            <h1
              className={tm(
                'mb-4 max-w-[400px] text-center text-4xl font-semibold leading-[40px] -tracking-[2px] text-black md:mb-8 md:max-w-[540px] md:text-[50px] md:leading-[50px] md:-tracking-[2.24px] lg:text-left xl:text-[56px] xl:leading-[56px] xl:-tracking-[2.24px]',
                !image && 'md:text-center lg:text-center',
                image && 'md:max-w-[550px]',
                titleClassName,
              )}
            >
              {title}
            </h1>
          ) : null}
          {description ? (
            <Markdown
              center={!image}
              className={tm(
                'mx-0 max-w-[400px] text-center text-base text-dark-gray md:max-w-[600px] md:text-lg lg:text-left',
                !image && 'md:text-center lg:text-center',
              )}
            >
              {description}
            </Markdown>
          ) : null}
          {primaryLink?.url || secondaryLink?.url ? (
            <div className="mt-8 flex w-full flex-col flex-wrap items-center justify-center gap-4 sm:flex-row lg:justify-start">
              {primaryLink?.url ? (
                <Button
                  href={primaryLink.url}
                  variant="primary"
                  className="max-h-[unset] rounded-full border-gray px-7 py-3 font-semibold"
                  innerClassName="text-center h-auto w-auto whitespace-nowrap"
                  locale={primaryLink.locale}
                >
                  {primaryLink.title}
                </Button>
              ) : null}
              {secondaryLink?.url ? (
                <Button
                  href={secondaryLink.url}
                  variant="secondary"
                  className="h-12 rounded-full border-gray px-7 py-3 font-semibold md:h-13"
                  innerClassName="text-center h-auto w-auto whitespace-nowrap"
                  locale={secondaryLink.locale}
                >
                  {secondaryLink.title}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {image ? (
          <div className="relative flex h-auto w-full max-w-md flex-1 justify-center md:hidden lg:flex lg:min-h-[400px] lg:max-w-lg">
            {'url' in image ? (
              <StrapiImage
                {...image}
                priority
                quality={100}
                className="inset-0 h-auto max-h-[400px] max-w-full rounded-3xl object-contain lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2"
              />
            ) : (
              <Image
                src={image.image}
                alt={image.alt}
                priority
                quality={100}
                className="rounded-3xl object-contain"
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
